import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../components/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import StatsCard from '../../components/common/StatsCard';
import {
  TrendingUp,
  TrendingDown,
  Activity,
  BarChart3,
  LineChart,
  PieChart,
} from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';

const TradingPerformance = () => {
  const performanceMetrics = {
    profitCurve: [
      { month: 'Jan', value: 12000 },
      { month: 'Feb', value: 15000 },
      { month: 'Mar', value: 13500 },
      { month: 'Apr', value: 18000 },
      { month: 'May', value: 22000 },
      { month: 'Jun', value: 25000 },
    ],
    monthlyReturns: [
      { month: 'Jan', return: 8.5 },
      { month: 'Feb', return: 12.3 },
      { month: 'Mar', return: -5.2 },
      { month: 'Apr', return: 15.7 },
      { month: 'May', return: 18.9 },
      { month: 'Jun', return: 22.1 },
    ],
    drawdown: {
      current: 8.5,
      max: 15.2,
      average: 6.8,
    },
    statistics: {
      winRate: 68.5,
      sharpeRatio: 2.3,
      averageTrade: 245,
      bestTrade: 1250,
      worstTrade: -320,
      totalTrades: 342,
      profitableTrades: 234,
    },
  };

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Trading Performance"
          description="Comprehensive performance metrics across all trading services"
        />
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="profit">Profit Curve</TabsTrigger>
          <TabsTrigger value="monthly">Monthly Returns</TabsTrigger>
          <TabsTrigger value="drawdown">Drawdown</TabsTrigger>
          <TabsTrigger value="statistics">Statistics</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatsCard
              title="Total Profit"
              value="$25,000"
              change="+108% from start"
              icon={TrendingUp}
            />
            <StatsCard
              title="Win Rate"
              value={`${performanceMetrics.statistics.winRate}%`}
              change="Above average"
              icon={Activity}
            />
            <StatsCard
              title="Sharpe Ratio"
              value={performanceMetrics.statistics.sharpeRatio.toFixed(2)}
              change="Excellent"
              icon={LineChart}
            />
            <StatsCard
              title="Total Trades"
              value={performanceMetrics.statistics.totalTrades.toString()}
              change="This period"
              icon={BarChart3}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <StatsCard
              title="Average Trade"
              value={`$${performanceMetrics.statistics.averageTrade}`}
              change="Per trade"
              icon={PieChart}
            />
            <StatsCard
              title="Best Trade"
              value={`$${performanceMetrics.statistics.bestTrade}`}
              change="Highest profit"
              icon={TrendingUp}
            />
            <StatsCard
              title="Worst Trade"
              value={`$${performanceMetrics.statistics.worstTrade}`}
              change="Maximum loss"
              icon={TrendingDown}
            />
          </div>
        </TabsContent>

        <TabsContent value="profit" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Profit Curve</CardTitle>
              <CardDescription>Cumulative profit over time</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64 flex items-center justify-center bg-muted rounded-lg">
                <p className="text-muted-foreground">
                  Chart visualization would be rendered here
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="monthly" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Monthly Returns</CardTitle>
              <CardDescription>Monthly performance breakdown</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {performanceMetrics.monthlyReturns.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <span className="font-medium">{item.month}</span>
                      <span
                        className={`text-sm font-semibold ${
                          item.return >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {item.return >= 0 ? '+' : ''}
                        {item.return.toFixed(2)}%
                      </span>
                    </div>
                    <div
                      className="h-2 bg-gray-200 rounded-full overflow-hidden"
                      style={{ width: '200px' }}
                    >
                      <div
                        className={`h-full ${
                          item.return >= 0 ? 'bg-green-500' : 'bg-red-500'
                        }`}
                        style={{
                          width: `${Math.abs(item.return)}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="drawdown" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Current Drawdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-red-600">
                  {performanceMetrics.drawdown.current}%
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  From peak equity
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Maximum Drawdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-orange-600">
                  {performanceMetrics.drawdown.max}%
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Historical maximum
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Average Drawdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-yellow-600">
                  {performanceMetrics.drawdown.average}%
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Across all periods
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="statistics" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Trade Statistics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Total Trades</span>
                  <span className="font-semibold">
                    {performanceMetrics.statistics.totalTrades}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">
                    Profitable Trades
                  </span>
                  <span className="font-semibold text-green-600">
                    {performanceMetrics.statistics.profitableTrades}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Losing Trades</span>
                  <span className="font-semibold text-red-600">
                    {performanceMetrics.statistics.totalTrades -
                      performanceMetrics.statistics.profitableTrades}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Win Rate</span>
                  <span className="font-semibold">
                    {performanceMetrics.statistics.winRate}%
                  </span>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Risk Metrics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Sharpe Ratio</span>
                  <span className="font-semibold">
                    {performanceMetrics.statistics.sharpeRatio.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Average Trade</span>
                  <span className="font-semibold">
                    ${performanceMetrics.statistics.averageTrade}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Best Trade</span>
                  <span className="font-semibold text-green-600">
                    ${performanceMetrics.statistics.bestTrade}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Worst Trade</span>
                  <span className="font-semibold text-red-600">
                    ${performanceMetrics.statistics.worstTrade}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TradingPerformance;
